import React, {useState, useEffect, useRef, useContext, Fragment} from 'react';
import {Button, Icon} from '@blueprintjs/core';
import {isEqual} from "lodash";

import {lineContext} from '/src/services/api';
import {errorToast} from '/src/utils/toaster';
import {Table, Row, Cell} from '/src/components/tables/table'
import {DataPageContext} from '/src/pages/data/context';

const IN_ACTION = [{direction: 'in', crossed: true}, {direction: 'out', crossed: false}];
const OUT_ACTION = [{direction: 'out', crossed: true}, {direction: 'in', crossed: false}];
const IN_AND_OUT_ACTION = [{direction: 'out', crossed: true}, {direction: 'in', crossed: true}];
import {ItemRow} from "./item-row";
import {EditingItemForm} from './editing-item-form';


// -------- helper functions
const sortActions = (actions) => {
    return actions.sort((a, b) => (a.direction > b.direction ? 1 : -1));
};

const getActionType = (actions) => {
    const actionsWithoutId = actions.map(({direction, crossed}) => {
        return {
            direction,
            crossed
        };
    });
    if (isEqual(sortActions(actionsWithoutId), sortActions(IN_ACTION))) return 'in';
    else if (isEqual(sortActions(actionsWithoutId), sortActions(OUT_ACTION))) return 'out';
    else if (isEqual(sortActions(actionsWithoutId), sortActions(IN_AND_OUT_ACTION))) return 'in_out';
    return undefined;
};

function LineContexts({
    recording,
    elems,
    taxonomiesInUse,
    keysAndValuesInUse: propKeysAndValuesInUse,
    loadingTaxonomies,
    updateEditRecordingPageState,
    setShapeHighlight,
}) {
    const {loading, setLoading} = useContext(DataPageContext);

    const [lineContexts, setLineContexts] = useState(recording?.lineContexts || []);
    const [addingItem, setAddingItem] = useState(false);
    const [editingExistedItem, setEditingExistedItem] = useState(null);
    const [keysAndValuesInUse, setKeysAndValuesInUse] = useState(propKeysAndValuesInUse);

    const needsSyncRef = useRef(false);

    // Initialise from props if state is null/undefined
    useEffect(() => {
        if (!lineContexts && recording?.lineContexts?.length)
            setLineContexts(recording.lineContexts);
    }, [recording?.lineContexts]);

    useEffect(() => {
        if (!keysAndValuesInUse && propKeysAndValuesInUse)
            setKeysAndValuesInUse(propKeysAndValuesInUse);
    }, [propKeysAndValuesInUse]);

    // Sync to recording + trigger save after state updates flagged by needsSyncRef
    useEffect(() => {
        if (needsSyncRef.current) {
            needsSyncRef.current = false;
            recording.lineContexts = lineContexts;
            updateEditRecordingPageState();
        }
    }, [lineContexts]);

    function beginAddingNewContext() {
        setAddingItem(!addingItem);
    }

    function stopAddingNewContext() {
        setAddingItem(false);
    }

    function validateContext(context) {
        let doThrow = false;
        if (!context.type) {
            errorToast({message: 'Missing a type'});
            doThrow = true;
        }
        if (
            context.type === 'within-location-movement' &&
            !context.withinLocationMovementKey
        ) {
            errorToast({message: 'Missing a withinLocationMovementKey'});
            doThrow = true;
        }
        if (doThrow) throw new Error('Invalid form');
    }

    async function saveNewContext(context) {
        const prevLocation = loading.location;

        // sanitize: strip _id and extra fields from actions and metadata before API call
        if (context.actions) {
            context.actions = context.actions.map(pair =>
                pair.map(({lineId, direction, crossed}) => ({lineId, direction, crossed}))
            );
        }
        if (context.metadata && (context.type === 'within-location-entry' || context.type === 'within-location-pass-by')) {
            context.metadata = context.metadata.map(({key, value}) => ({key, value}));
        } else {
            delete context.metadata;
        }
        if (context.type !== 'within-location-movement') {
            delete context.withinLocationMovementKey;
        }

        setLoading({status: true, location: "Saving line context..."});
        // do api call — use context.id to distinguish update vs create
        if (context.id) {
            try {
                validateContext(context);
                const response = await lineContext.update(
                    context,
                    recording.id
                );

                if (response) {
                    const filteredLineContexts = lineContexts.reduce(
                        (finalLineContexts, lContext) => {
                            if (lContext.id !== context.id) {
                                finalLineContexts.push(lContext);
                            }
                            return finalLineContexts;
                        },
                        []
                    );

                    needsSyncRef.current = true;
                    setAddingItem(false);
                    setEditingExistedItem(null);
                    setLineContexts([...filteredLineContexts, response]);
                } else {
                    setAddingItem(false);
                    setEditingExistedItem(null);
                }
            } catch (e) {
                // validation or API error
            } finally {
                setLoading({status: false, location: prevLocation});
            }
        } else {
            try {
                validateContext(context);
                const response = await lineContext.create(
                    context,
                    recording.id
                );

                if (response) {
                    needsSyncRef.current = true;
                    setAddingItem(false);
                    setLineContexts([...lineContexts, response]);
                } else {
                    setAddingItem(false);
                }
            } catch (e) {
                // validation or API error
            } finally {
                setLoading({status: false, location: prevLocation});
            }
        }
    }

    function deleteContext(context) {
        if (window.confirm('Are you sure you want to delete this lineContext?')) {
            const prevLocation = loading.location;
            setLoading({status: true, location: "Deleting line context..."});
            if (context.id) {
                lineContext.delete(context.id).then(() => {
                    needsSyncRef.current = true;
                    setLineContexts([
                        ...lineContexts.filter(lc => lc.id !== context.id)
                    ]);
                }).catch((e) => {
                    errorToast({message: 'Failed to delete line context'});
                }).finally(() => {
                    setLoading({status: false, location: prevLocation});
                })
            } else {
                setLoading({status: false, location: prevLocation});
            }
        }
    }

    function handleBeingEditedLineContext(context) {
        setEditingExistedItem(context);
    }

    return (
        <Table>
            <Row>
                <Cell haligh="left" valign="center" span={2}>
                    {lineContexts?.length ?? "No"} line contexts
                </Cell>
                <Cell span={1}>
                    <Button
                        icon="plus"
                        text="Add New"
                        fill={true}
                        onClick={beginAddingNewContext}
                    />
                </Cell>
            </Row>
            <Row $separatorColor="silver">
                <Cell header span={2} halign="center">Type</Cell>
                <Cell header span={3} halign="center">Actions</Cell>
                <Cell header span={3} halign="center">Context</Cell>
                <Cell header span={1} halign="center"><Icon icon="more"/></Cell>
            </Row>
            {lineContexts &&
                lineContexts
                    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
                    .map((lc, index) => {
                        if (editingExistedItem && editingExistedItem.id === lc.id)
                            return <EditingItemForm
                                key={lc.id}
                                item={editingExistedItem}
                                keysAndValuesInUse={keysAndValuesInUse}
                                elems={elems}
                                cancelEditing={() => handleBeingEditedLineContext(null)}
                                editItem={handleBeingEditedLineContext}
                                saveItem={saveNewContext}
                                setShapeHighlight={setShapeHighlight}
                                getActionType={getActionType}
                                loadingTaxonomies={loadingTaxonomies}
                            />
                        else
                            return <ItemRow
                                key={lc.id}
                                elems={elems}
                                setShapeHighlight={setShapeHighlight}
                                lineContext={lc}
                                deleteContext={deleteContext}
                                editItem={handleBeingEditedLineContext}
                                getActionType={getActionType}
                            />
                    })}
            {addingItem && (
                <Row><Cell halign="center"><h4>Adding new item</h4></Cell></Row>
            )}
            {addingItem && (
                <Fragment>
                    <EditingItemForm
                        item={{}}
                        keysAndValuesInUse={keysAndValuesInUse}
                        elems={elems}
                        cancelEditing={() => {
                            handleBeingEditedLineContext(null)
                            setAddingItem(false);
                        }}
                        editItem={handleBeingEditedLineContext}
                        saveItem={saveNewContext}
                        setShapeHighlight={setShapeHighlight}
                        getActionType={getActionType}
                        loadingTaxonomies={loadingTaxonomies}
                    />
                </Fragment>
            )}
        </Table>
    );
}

export {LineContexts};
